import React, { useEffect, useState, useMemo, useContext } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Users, Building2, Wrench, AlertCircle } from 'lucide-react';
import { API, AuthContext } from '../App';
import BuildingFilter from '../components/BuildingFilter';
import { toast } from 'sonner';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function startOfMonth(d) { const x = new Date(d.getFullYear(), d.getMonth(), 1); return x; }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

export default function PpmCalendar() {
  const { user } = useContext(AuthContext);
  const [cursor, setCursor] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [providers, setProviders] = useState([]);
  const [filterBuildingId, setFilterBuildingId] = useState('all');
  const [track, setTrack] = useState('all');           // all | inhouse | provider | emergency

  const fetchAll = async () => {
    try {
      const params = filterBuildingId !== 'all' ? { building_id: filterBuildingId } : {};
      const [t, s, b, st, sp] = await Promise.all([
        axios.get(`${API}/ppm-tasks`, { params, withCredentials: true }),
        axios.get(`${API}/work-schedules`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/buildings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/staff`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/suppliers`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTasks(t.data || []); setSchedules(s.data || []);
      setBuildings(b.data || []); setStaff(st.data || []); setProviders(sp.data || []);
    } catch { toast.error('Failed to load calendar data'); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [filterBuildingId]);

  // Bucket tasks by date string for the visible month
  const buckets = useMemo(() => {
    const m = {};
    const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    const push = (date, item) => {
      if (!date) return;
      const key = date.slice(0, 10);
      if (!key.startsWith(monthKey)) return;
      const trackOf = item._track || 'inhouse';
      if (track !== 'all' && track !== trackOf) return;
      m[key] = m[key] || [];
      m[key].push(item);
    };
    tasks.forEach(t => push(t.due_date, { ...t, _track: t.assigned_to_provider ? 'provider' : 'inhouse', _kind: 'ppm' }));
    schedules.forEach(s => push(s.next_due, { ...s, _track: s.work_type === 'security' ? 'provider' : 'inhouse', _kind: 'sched' }));
    return m;
  }, [tasks, schedules, cursor, track]);

  const monthName = cursor.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  const firstDow = startOfMonth(cursor).getDay();
  const total = daysInMonth(cursor);

  // counts
  const summary = useMemo(() => {
    const inhouse = tasks.filter(t => !t.assigned_to_provider).length;
    const prov = tasks.filter(t => t.assigned_to_provider).length;
    const overdue = tasks.filter(t => t.due_date && t.due_date < new Date().toISOString() && t.status !== 'completed').length;
    return { inhouse, prov, overdue };
  }, [tasks]);

  return (
    <Layout>
      <div data-testid="ppm-calendar-page" className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <CalendarIcon className="w-6 h-6 text-cyan-600" />PPM Calendar
            </h1>
            <p className="text-sm text-muted-foreground">Upcoming preventive-maintenance tasks for in-house team and service providers — drag-style overview with status tracking.</p>
          </div>
          <BuildingFilter value={filterBuildingId} onChange={setFilterBuildingId} buildings={buildings} />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Card className="p-3"><div className="text-xs text-muted-foreground">In-house</div><div className="text-2xl font-bold text-cyan-700 flex items-center gap-1"><Users className="w-5 h-5" />{summary.inhouse}</div></Card>
          <Card className="p-3"><div className="text-xs text-muted-foreground">Service provider</div><div className="text-2xl font-bold text-amber-700 flex items-center gap-1"><Wrench className="w-5 h-5" />{summary.prov}</div></Card>
          <Card className="p-3"><div className="text-xs text-muted-foreground">Overdue</div><div className="text-2xl font-bold text-rose-700 flex items-center gap-1"><AlertCircle className="w-5 h-5" />{summary.overdue}</div></Card>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" variant="outline" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-lg font-semibold text-foreground min-w-[180px] text-center">{monthName}</div>
          <Button size="sm" variant="outline" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <div className="ml-auto flex gap-1 p-0.5 rounded-md bg-slate-100">
            {[{ k: 'all', label: 'All' }, { k: 'inhouse', label: 'In-house' }, { k: 'provider', label: 'Provider' }].map(o => (
              <button key={o.k} data-testid={`cal-track-${o.k}`} onClick={() => setTrack(o.k)} className={`px-3 py-1 rounded text-xs font-medium ${track === o.k ? 'bg-white text-cyan-700 shadow' : 'text-slate-600'}`}>{o.label}</button>
            ))}
          </div>
        </div>

        <Card className="p-2 overflow-hidden">
          <div className="grid grid-cols-7 text-xs text-muted-foreground mb-1">
            {DAY_LABELS.map(d => <div key={d} className="px-2 py-1 text-center font-semibold">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDow }).map((_, i) => <div key={`pad-${i}`} className="min-h-24" />)}
            {Array.from({ length: total }).map((_, i) => {
              const d = i + 1;
              const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
              const events = buckets[dateStr] || [];
              const isToday = dateStr === new Date().toISOString().slice(0, 10);
              return (
                <div key={dateStr} data-testid={`cal-day-${d}`} className={`min-h-24 rounded-md border p-1 text-xs ${isToday ? 'border-amber-400 bg-amber-50/30' : 'border-slate-200'}`}>
                  <div className="text-right text-[10px] text-slate-500 mb-0.5">{d}</div>
                  <div className="space-y-0.5">
                    {events.slice(0, 4).map((ev, idx) => {
                      const cls = ev._track === 'provider' ? 'bg-amber-100 text-amber-800' : 'bg-cyan-100 text-cyan-800';
                      return <div key={idx} className={`px-1 py-0.5 rounded truncate ${cls}`} title={ev.title}>{ev.title}</div>;
                    })}
                    {events.length > 4 && <div className="text-[10px] text-slate-500">+{events.length - 4} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
